/**
 * EffectsPresetManagementMenu Component
 * 
 * Context menu for managing individual presets.
 * Provides options to rename, delete, duplicate, toggle favorite, and export/import presets.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { effectsPresetService, type EffectsChainPreset } from '../../services/effects/EffectsPresetService';
import { announceToScreenReader } from '../../utils/accessibility';
import './EffectsPresetManagementMenu.css';

/**
 * Props for EffectsPresetManagementMenu component
 */
export interface EffectsPresetManagementMenuProps {
  /** The preset to manage */
  preset: EffectsChainPreset;
  /** Whether the menu is open */
  isOpen: boolean;
  /** Callback to close the menu */
  onClose: () => void;
  /** Callback when preset is updated */
  onPresetUpdated?: () => void;
  /** Callback when preset is deleted */
  onPresetDeleted?: () => void;
}

/**
 * EffectsPresetManagementMenu component
 * 
 * Renders a context menu with:
 * - Rename preset
 * - Toggle favorite
 * - Duplicate preset
 * - Delete preset
 * - Export preset (optional)
 * - Import preset (optional)
 * 
 * @param props - Component props
 * @returns EffectsPresetManagementMenu component
 */
export function EffectsPresetManagementMenu({
  preset,
  isOpen,
  onClose,
  onPresetUpdated,
  onPresetDeleted,
}: EffectsPresetManagementMenuProps) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const menuRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  /**
   * Reset state when menu opens
   */
  useEffect(() => {
    if (isOpen) {
      setIsRenaming(false);
      setNewName(preset.name);
      setIsDeleting(false);
      setError(null);
    }
  }, [isOpen, preset.name]);

  /**
   * Focus rename input when entering rename mode
   */
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  /**
   * Handle escape key to close menu
   */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (isRenaming) {
          setIsRenaming(false);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, isRenaming, onClose]);

  /**
   * Handle click outside menu to close
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen, onClose]);

  /**
   * Handle rename
   */
  const handleRename = useCallback(async () => {
    if (!newName.trim()) {
      setError('Preset name cannot be empty');
      return;
    }

    try {
      await effectsPresetService.updatePreset(preset.id, {
        name: newName.trim(),
      });

      announceToScreenReader(
        `Preset renamed to "${newName.trim()}"`,
        'polite'
      );

      setIsRenaming(false);
      if (onPresetUpdated) {
        onPresetUpdated();
      }
      onClose();
    } catch (err) {
      console.error('Failed to rename preset:', err);
      setError(err instanceof Error ? err.message : 'Failed to rename preset');
      announceToScreenReader('Failed to rename preset', 'assertive');
    }
  }, [preset.id, newName, onPresetUpdated, onClose]);

  /**
   * Handle toggle favorite
   */
  const handleToggleFavorite = useCallback(async () => {
    try {
      await effectsPresetService.toggleFavorite(preset.id);

      announceToScreenReader(
        preset.isFavorite
          ? `Removed "${preset.name}" from favorites`
          : `Added "${preset.name}" to favorites`,
        'polite'
      );

      if (onPresetUpdated) {
        onPresetUpdated();
      }
      onClose();
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
      setError(err instanceof Error ? err.message : 'Failed to update favorite status');
      announceToScreenReader('Failed to update favorite status', 'assertive');
    }
  }, [preset.id, preset.name, preset.isFavorite, onPresetUpdated, onClose]);

  /**
   * Handle duplicate
   */
  const handleDuplicate = useCallback(async () => {
    try {
      const duplicated = await effectsPresetService.duplicatePreset(preset.id);

      announceToScreenReader(
        `Created duplicate preset "${duplicated.name}"`,
        'polite'
      );

      if (onPresetUpdated) {
        onPresetUpdated();
      }
      onClose();
    } catch (err) {
      console.error('Failed to duplicate preset:', err);
      setError(err instanceof Error ? err.message : 'Failed to duplicate preset');
      announceToScreenReader('Failed to duplicate preset', 'assertive');
    }
  }, [preset.id, onPresetUpdated, onClose]);

  /**
   * Handle delete
   */
  const handleDelete = useCallback(async () => {
    if (!isDeleting) {
      setIsDeleting(true);
      return;
    }

    try {
      await effectsPresetService.deletePreset(preset.id);

      announceToScreenReader(
        `Deleted preset "${preset.name}"`,
        'polite'
      );

      if (onPresetDeleted) {
        onPresetDeleted();
      }
      onClose();
    } catch (err) {
      console.error('Failed to delete preset:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete preset');
      announceToScreenReader('Failed to delete preset', 'assertive');
    }
  }, [preset.id, preset.name, isDeleting, onPresetDeleted, onClose]);

  /**
   * Handle export preset
   */
  const handleExport = useCallback(() => {
    try {
      const presetData = JSON.stringify(preset, null, 2);
      const blob = new Blob([presetData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${preset.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_preset.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      announceToScreenReader(
        `Exported preset "${preset.name}"`,
        'polite'
      );

      onClose();
    } catch (err) {
      console.error('Failed to export preset:', err);
      setError(err instanceof Error ? err.message : 'Failed to export preset');
      announceToScreenReader('Failed to export preset', 'assertive');
    }
  }, [preset, onClose]);

  if (!isOpen) return null;

  return (
    <div className="preset-menu-overlay" role="dialog" aria-modal="true" aria-label="Preset management menu">
      <div className="preset-menu" ref={menuRef}>
        {/* Error message */}
        {error && (
          <div className="preset-menu-error" role="alert">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
            <span>{error}</span>
          </div>
        )}

        {/* Rename mode */}
        {isRenaming ? (
          <div className="preset-menu-rename">
            <input
              ref={renameInputRef}
              type="text"
              className="preset-menu-rename-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRename();
                } else if (e.key === 'Escape') {
                  setIsRenaming(false);
                }
              }}
              placeholder="Enter new name"
              maxLength={50}
              aria-label="New preset name"
            />
            <div className="preset-menu-rename-actions">
              <button
                className="preset-menu-btn preset-menu-btn-small preset-menu-btn-primary"
                onClick={handleRename}
                type="button"
              >
                <i className="fas fa-check" aria-hidden="true"></i>
              </button>
              <button
                className="preset-menu-btn preset-menu-btn-small preset-menu-btn-secondary"
                onClick={() => setIsRenaming(false)}
                type="button"
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Menu items */}
            <button
              className="preset-menu-item"
              onClick={() => setIsRenaming(true)}
              type="button"
            >
              <i className="fas fa-edit" aria-hidden="true"></i>
              <span>Rename</span>
            </button>

            <button
              className="preset-menu-item"
              onClick={handleToggleFavorite}
              type="button"
            >
              <i className={`fas fa-star ${preset.isFavorite ? 'preset-menu-favorite' : ''}`} aria-hidden="true"></i>
              <span>{preset.isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}</span>
            </button>

            <button
              className="preset-menu-item"
              onClick={handleDuplicate}
              type="button"
            >
              <i className="fas fa-copy" aria-hidden="true"></i>
              <span>Duplicate</span>
            </button>

            <button
              className="preset-menu-item"
              onClick={handleExport}
              type="button"
            >
              <i className="fas fa-download" aria-hidden="true"></i>
              <span>Export</span>
            </button>

            <div className="preset-menu-divider" role="separator"></div>

            {/* Delete with confirmation */}
            {isDeleting ? (
              <div className="preset-menu-delete-confirm">
                <div className="preset-menu-delete-text">
                  <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
                  <span>Delete "{preset.name}"?</span>
                </div>
                <div className="preset-menu-delete-actions">
                  <button
                    className="preset-menu-btn preset-menu-btn-danger"
                    onClick={handleDelete}
                    type="button"
                  >
                    Delete
                  </button>
                  <button
                    className="preset-menu-btn preset-menu-btn-secondary"
                    onClick={() => setIsDeleting(false)}
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="preset-menu-item preset-menu-item-danger"
                onClick={handleDelete}
                type="button"
              >
                <i className="fas fa-trash" aria-hidden="true"></i>
                <span>Delete</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
